import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { GPKeyInitiativesEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/gpKeyInitiatives/components/partnerView/components/gpKeyInitiativesEditor/GPKeyInitiativesEditor'
import { KeyInitiativeEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import { IFormKIEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/models'
import { selectFinancialYear } from 'legacy/lut/features/partner360/configuration'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { getUrlWithParams } from 'core-components/helpers/url'
import MeasureController from 'core/analytics/MeasureController'
import { useNavigate } from 'core/hooks/useNavigate'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'
import { FINANCIAL_SECTION_URL_VALUE } from 'partner360/configs'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { USE_SELECTIVE_COPY_FROM_PREVIOUS_YEAR } from 'partner360/features/keyInitiatives/configs/config'
import { TKeyInitiativesFinancialData } from 'partner360/features/keyInitiatives/model/financialData'
import { scrollToElement } from 'partner360/helpers/scrollToElement'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { TRANSLATION_KEYS } from '../../configs/translations'
import { CopyKeyInitiativesFromPreviousYearDialog } from '../../copyFromPreviousYear'
import { useCopyFromPrevYear } from '../hooks/useCopyFromPrevYear'
import { useHandleShowAddNewDialog } from '../hooks/useHandleShowAddNewDialog'
import { useNewKeyInitiative } from '../hooks/useNewKeyInitiative'

import { getActionCards } from './helpers/getActionsCard'
import { ActionCardType } from './models'
import { RequirementsGetStarted } from './RequirementsGetStarted'
import { RequirementsOverview } from './RequirementsOverview'

import styles from './Requirements.module.scss'

interface IRequirementsProps {
  metrics?: MeasureController
  partnerId: string
  finData: TKeyInitiativesFinancialData[]
  isFinancialDataLoaded?: boolean
  activeCurrency: string
  actualsType: ActualsValueTypes
  refetchKI: () => void
  isGlobal: boolean
  programType: ProgramTypes
  onInitiativeCreate: () => void
  onInitiativesCopied?: () => void
}

export const Requirements = ({
  metrics,
  partnerId,
  refetchKI,
  finData,
  isFinancialDataLoaded = true,
  activeCurrency,
  actualsType,
  isGlobal,
  programType,
  onInitiativeCreate,
  onInitiativesCopied,
}: IRequirementsProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const currentFiscalYear = useSelector(selectFinancialYear)

  const navigate = useNavigate()

  const actionCards = getActionCards({
    isFinancialPlanCompleted: Boolean(finData[0]?.PlansCYNdp),
    isFinancialDataLoaded,
  })

  const { showAddNewDialog, handleShowNewDialog, handleCloseNewDialog } =
    useHandleShowAddNewDialog()

  const { sourceItem, resetSourceItem } = useNewKeyInitiative<IFormKIEditor>({
    handleShowNewDialog,
  })

  const {
    showCopyFromPrevYearDialog,
    openCopyFromPrevYearDialog,
    closeCopyFromPrevYearDialog,
    handleCopyFromPrevYear,
  } = useCopyFromPrevYear({ onChangeLoading: setIsLoading, partnerId, fetch: refetchKI })

  const closeNewKIDialog = useCallback(() => {
    handleCloseNewDialog()
    resetSourceItem()
  }, [handleCloseNewDialog, resetSourceItem])

  const handleActionCardClick = useCallback(
    (action: ActionCardType) => {
      switch (action) {
        case ActionCardType.CreateInitiative:
          handleShowNewDialog()
          break

        case ActionCardType.CompleteFinancialPlan: {
          const newUrl = getUrlWithParams({
            'jbp-tab': FINANCIAL_SECTION_URL_VALUE,
          })

          navigate(newUrl)
          scrollToElement(p360TabsIds)
          break
        }

        case ActionCardType.ClonePreviousYear:
          openCopyFromPrevYearDialog()
          break

        default:
          break
      }
    },
    [handleShowNewDialog, openCopyFromPrevYearDialog, navigate]
  )

  const initiativeCreateHandler = useCallback(() => {
    onInitiativeCreate()
    closeNewKIDialog()
  }, [closeNewKIDialog, onInitiativeCreate])

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading} />
      <RequirementsOverview />

      <RequirementsGetStarted cards={actionCards} onAction={handleActionCardClick} />
      <RenderWhen condition={showAddNewDialog}>
        {isGlobal ? (
          <GPKeyInitiativesEditor
            initialState={sourceItem}
            isDialogOpened={showAddNewDialog}
            isDataInsightPresent={programType !== ProgramTypes.Retail}
            onLoadingStateChange={setIsLoading}
            onEditCancel={closeNewKIDialog}
            onUpdateFinished={initiativeCreateHandler}
            metrics={metrics}
          />
        ) : (
          <KeyInitiativeEditor
            initialState={sourceItem}
            isDialogOpened={showAddNewDialog}
            isDataInsightPresent={programType !== ProgramTypes.Retail}
            onLoadingStateChange={setIsLoading}
            onEditCancel={closeNewKIDialog}
            onUpdateFinished={initiativeCreateHandler}
            metrics={metrics}
          />
        )}
      </RenderWhen>

      {USE_SELECTIVE_COPY_FROM_PREVIOUS_YEAR ? (
        <CopyKeyInitiativesFromPreviousYearDialog
          show={showCopyFromPrevYearDialog}
          partnerId={partnerId}
          programType={programType}
          currentFiscalYear={currentFiscalYear}
          activeCurrency={activeCurrency}
          actualsType={actualsType}
          onClose={closeCopyFromPrevYearDialog}
          onDataChanged={refetchKI}
          onCopied={onInitiativesCopied ?? onInitiativeCreate}
        />
      ) : (
        <ConfirmationDialog
          onConfirm={handleCopyFromPrevYear}
          onReject={closeCopyFromPrevYearDialog}
          title={TRANSLATION_KEYS.requirements.copyKIDialog.title}
          content={TRANSLATION_KEYS.requirements.copyKIDialog.text}
          confirmBtnText={TRANSLATION_KEYS.requirements.copyKIDialog.submit}
          rejectBtnText={TRANSLATION_KEYS.requirements.copyKIDialog.cancel}
          isVisible={showCopyFromPrevYearDialog}
        />
      )}
    </div>
  )
}
